import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './RewardRedeemedPage.css';
import logo from '../../../shared/assets/images/logo.png';
import coffeeLogo from '../../../Exampledata/logoexamplecoffee.png';
import rewardData from '../../../Exampledata/rewardData.json';

const RewardRedeemedPage = () => {
  const navigate = useNavigate();

  // Function to darken a hex color
  const darkenColor = (hex, amount = 0.3) => {
    // Remove # if present
    hex = hex.replace('#', '');
    
    // Parse RGB values
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Darken each component
    const darkenedR = Math.max(0, Math.floor(r * (1 - amount)));
    const darkenedG = Math.max(0, Math.floor(g * (1 - amount)));
    const darkenedB = Math.max(0, Math.floor(b * (1 - amount)));
    
    // Convert back to hex
    return `#${darkenedR.toString(16).padStart(2, '0')}${darkenedG.toString(16).padStart(2, '0')}${darkenedB.toString(16).padStart(2, '0')}`;
  };

  // Function to determine if a color is light or dark
  const isLightColor = (hex) => {
    // Remove # if present
    hex = hex.replace('#', '');
    
    // Parse RGB values
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Calculate luminance using the relative luminance formula
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    // Return true if luminance is greater than 0.5 (light color)
    return luminance > 0.5;
  };
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [transform, setTransform] = useState('');
  const [shadow, setShadow] = useState('');
  const [isPressed, setIsPressed] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [isFlipped, setIsFlipped] = useState(false);
  const [countdown, setCountdown] = useState(180); // 3 minutes in seconds
  const [isCountdownActive, setIsCountdownActive] = useState(false);
  const cardRef = React.useRef(null);

  useEffect(() => {
    // Simulate loading the data
    setTimeout(() => {
      setData(rewardData);
      setLoading(false);
      // Show celebration animation after loading
      setTimeout(() => {
        setShowCelebration(true);
      }, 300);
    }, 500);
  }, []);

  // Countdown effect
  useEffect(() => {
    if (isCountdownActive && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0) {
      setIsCountdownActive(false);
      navigate('/earned');
    }
  }, [isCountdownActive, countdown, navigate]);

  // Touch-based parallax effect
  const handleTouchMove = (e) => {
    if (!cardRef.current || !isPressed) return;
    
    const rect = cardRef.current.getBoundingClientRect();
    const touch = e.touches[0];
    const x = ((touch.clientX - rect.left) / rect.width - 0.5) * 15;
    const y = ((touch.clientY - rect.top) / rect.height - 0.5) * 15;
    
    setTransform(`perspective(1000px) rotateY(${x}deg) rotateX(${-y}deg) translateZ(20px)`);
    
    // Dynamic shadow based on tilt
    const shadowX = x * 2; // Shadow moves opposite to tilt
    const shadowY = Math.abs(y) * 1.5 + 20; // Shadow gets larger with more tilt
    const shadowBlur = Math.abs(x) + Math.abs(y) + 40; // More blur with more tilt
    const shadowOpacity = 0.4 + (Math.abs(x) + Math.abs(y)) * 0.02; // Darker with more tilt
    
    setShadow(`
      ${-shadowX}px ${shadowY}px ${shadowBlur}px rgba(255, 163, 102, ${shadowOpacity}),
      ${-shadowX * 0.5}px ${shadowY * 0.7}px ${shadowBlur * 0.6}px rgba(0, 0, 0, 0.15),
      ${-shadowX * 0.3}px ${shadowY * 0.4}px ${shadowBlur * 0.3}px rgba(0, 0, 0, 0.1)
    `);
  };

  const handleTouchStart = (e) => {
    setIsPressed(true);
    handleTouchMove(e);
  };

  const handleTouchEnd = () => {
    setIsPressed(false);
    // Return to neutral position with smooth transition
    setTransform('perspective(1000px) rotateY(0deg) rotateX(0deg) translateZ(0px)');
    setShadow('');
  };

  // Mouse events for desktop testing
  const handleMouseDown = (e) => {
    setIsPressed(true);
    handleMouseMove(e);
  };

  const handleMouseMove = (e) => {
    if (!cardRef.current || !isPressed) return;
    
    const rect = cardRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width - 0.5) * 15;
    const y = ((e.clientY - rect.top) / rect.height - 0.5) * 15;
    
    setTransform(`perspective(1000px) rotateY(${x}deg) rotateX(${-y}deg) translateZ(20px)`);
    
    // Dynamic shadow for mouse as well
    const shadowX = x * 2;
    const shadowY = Math.abs(y) * 1.5 + 20;
    const shadowBlur = Math.abs(x) + Math.abs(y) + 40;
    const shadowOpacity = 0.4 + (Math.abs(x) + Math.abs(y)) * 0.02;
    
    setShadow(`
      ${-shadowX}px ${shadowY}px ${shadowBlur}px rgba(255, 163, 102, ${shadowOpacity}),
      ${-shadowX * 0.5}px ${shadowY * 0.7}px ${shadowBlur * 0.6}px rgba(0, 0, 0, 0.15),
      ${-shadowX * 0.3}px ${shadowY * 0.4}px ${shadowBlur * 0.3}px rgba(0, 0, 0, 0.1)
    `);
  };

  const handleMouseUp = () => {
    setIsPressed(false);
    setTransform('perspective(1000px) rotateY(0deg) rotateX(0deg) translateZ(0px)');
    setShadow('');
  };

  const handleMouseLeave = () => {
    setIsPressed(false);
    setTransform('perspective(1000px) rotateY(0deg) rotateX(0deg) translateZ(0px)');
    setShadow('');
  };

  if (loading) {
    return (
      <div className="loading">
        <p style={{ 
          fontFamily: 'Poppins, sans-serif', 
          color: '#000000',
          fontSize: '18px',
          fontWeight: '500',
          marginBottom: '20px'
        }}>
          Loading Rewards...
        </p>
        <img 
          src={logo} 
          alt="Bounce Loyalty" 
          style={{ 
            width: '120px', 
            height: 'auto',
            display: 'block'
          }} 
        />
      </div>
    );
  }

  const { businessName, rewardType, color } = data;
  
  // Determine text color based on background color
  const textColor = isLightColor(color || '#FFA366') ? '#666666' : 'white';

  const handleShowToCashier = () => {
    setIsFlipped(true);
    setIsCountdownActive(true);
  };

  const handleRedeemLater = () => {
    navigate('/earned');
  };

  const handleBounceLogoClick = () => {
    navigate('/');
  };

  // Format countdown time as MM:SS
  const formatCountdown = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Get current date formatted
  const getCurrentDate = () => {
    const now = new Date();
    return now.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="reward-redeemed-page">
      <div 
        ref={cardRef}
        className={`reward-container ${isFlipped ? 'flipped' : ''}`}
        style={{ 
          transform: transform,
          boxShadow: shadow
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
      >
        <div className="card-inner">
          <div 
            className="card-front"
            style={{ backgroundColor: color || '#FFA366' }}
          >
            <div className="header">
              <img src={coffeeLogo} alt="Coffee Shop" className="coffee-logo" />
            </div>

            <div className="redeemed-container">
              <div className={`checkmark-circle ${showCelebration ? 'animate' : ''}`}>
                <div className="checkmark">
                  <svg 
                    width="180" 
                    height="180" 
                    viewBox="0 0 160 160" 
                    style={{ 
                      background: 'transparent',
                      border: 'none',
                      outline: 'none'
                    }}
                  >
                    <circle 
                      cx="80" 
                      cy="80" 
                      r="70" 
                      fill="none" 
                      stroke={textColor} 
                      strokeWidth="10" 
                      opacity="0.4"
                    />
                    <path 
                      d="M50 80 L70 100 L110 60" 
                      fill="none" 
                      stroke={textColor} 
                      strokeWidth="8" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                      className={`checkmark-path ${showCelebration ? 'animate' : ''}`}
                    />
                  </svg>
                </div>
              </div>
              
              <div className={`redeemed-text ${showCelebration ? 'slide-up' : ''}`}>
                <p style={{ color: textColor, opacity: 0.9 }}>
                  When you're ready, tap your phone with the webpage open and show the cashier to redeem.
                </p>
              </div>
            </div>


            <div className="action-buttons">
              <button 
                className="show-cashier-button"
                style={{ color: color ? darkenColor(color, 0.4) : '#B8732E' }}
                onClick={handleRedeemLater}
              >
                REDEEM LATER
              </button>
            </div>

          </div>
          
          <div 
            className="card-back"
            style={{ backgroundColor: color || '#FFA366' }}
          >
            <div className="card-back-content">
              <div className="header">
                <img src={coffeeLogo} alt="Coffee Shop" className="coffee-logo" />
              </div>
              
              <div className="reward-info">
                <h2 style={{ color: textColor }}>{rewardType} at {businessName}</h2>
              </div>
              
              <div className="countdown-display">
                <div className="countdown-timer" style={{ color: textColor }}>
                  <span className="countdown-label">Expires in:</span>
                  <span className="countdown-time">{formatCountdown(countdown)}</span>
                </div>
              </div>
              
              <div className="reward-date" style={{ color: textColor, opacity: 0.9 }}>
                <span className="date-label">Redeemed on:</span>
                <span className="date-value">{getCurrentDate()}</span>
              </div>
              
              <div className="instructions" style={{ color: textColor, opacity: 0.8, marginTop: '10px' }}>
                Show this screen to the cashier to complete your redemption
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bounce-logo-container">
        <img 
          src={logo} 
          alt="Powered by BounceLoyalty" 
          className="bounce-logo" 
          onClick={handleBounceLogoClick}
        />
      </div>

      <div className="legal-links">
        <a href="/privacy-policy" className="legal-link">Privacy Policy</a>
        <span className="legal-separator">•</span>
        <a href="/terms-of-service" className="legal-link">Terms of Service</a>
      </div>
    </div>
  );
};

export default RewardRedeemedPage;